/**
 * Stream message handlers using Pinia store.
 * MQTT deltas go directly to venueDataStore.
 */

import type { Router } from "vue-router";
import { ToastSeverity } from "primevue/api";
import { useToastService } from "@/composables/useToastService.js";
import { type ViewContextStore } from "@/stores/viewContext";
import { useVenueDataStore } from "@/stores/venueData";
import { useInstallerMapStore } from "@/stores/installerMap";
import type { BeaconPlacementLocation } from "@/types/api";
import type { EdgeStreamMessage, EventLogEntry } from "@/types/streaming";

// ================================================================
// Edge Stream Types
// ================================================================

/**
 * Unified Edge Stream Message structure (stream/edge topic)
 * Provides delta updates with UUID-based message tracking
 */
// (EdgeStreamMessage moved to @/types/streaming)

// ================================================================
// Edge Stream Handler
// ================================================================

/**
 * Last uuid seen per publisher (message.uniqueId). Publishers interleave on one
 * topic but each chains last_uuid only to its own messages, so a single global
 * cursor reads every interleaving as a gap.
 */
const lastUuidByChain = new Map<string, string>();

const eventLogListeners = new Set<(entry: EventLogEntry) => void>();
const recoveryListeners = new Set<() => void>();

/** Subscribe to event log rows from scope=event messages; returns an unsubscribe function. */
export function onEventLogEntry(
    listener: (entry: EventLogEntry) => void,
): () => void {
    eventLogListeners.add(listener);
    return () => eventLogListeners.delete(listener);
}

/** Subscribe to stream recoveries (messages may have been missed); returns an unsubscribe function. */
export function onStreamRecovered(listener: () => void): () => void {
    recoveryListeners.add(listener);
    return () => recoveryListeners.delete(listener);
}

// A throwing listener must not stop the others or the message handling around it.
function notify<A extends unknown[]>(
    listeners: Set<(...args: A) => void>,
    ...args: A
) {
    for (const listener of listeners) {
        try {
            listener(...args);
        } catch (error) {
            console.error("Stream listener failed:", error);
        }
    }
}

export function streamChainKey(message: Pick<EdgeStreamMessage, "uniqueId">) {
    return message.uniqueId ?? "";
}

/**
 * Whether the user is told a recovery happened; both kinds refetch identically.
 * `stream-interrupted` = the stream provably broke (gap, reconnect, network
 * loss), so say so. `precautionary` = the tab refocused and nothing is known to
 * be wrong, so refetch silently rather than announce a phantom reconnection.
 */
export type StreamRecoverySeverity = "stream-interrupted" | "precautionary";

/**
 * One recovery per window, shared by every caller so gap detection and the
 * transport events that usually accompany it can't double-fetch. Suppressed
 * recoveries are deferred, never dropped.
 */
const GAP_RECOVERY_COOLDOWN_MS = 10_000;
let gapRecoveryCooldownUntil = 0;

/** A recovery that arrived mid-cooldown, waiting for the window to expire. */
let pendingRecovery: {
    store: ViewContextStore;
    reason: string;
    severity: StreamRecoverySeverity;
} | null = null;
let pendingRecoveryTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Queue a recovery for the end of the cooldown, collapsed to a single run.
 * Dropping it instead loses state whenever a connection flaps twice inside one
 * window: the second outage would never be fetched.
 */
function deferRecovery(
    store: ViewContextStore,
    reason: string,
    severity: StreamRecoverySeverity,
) {
    // Keep the most severe queued reason, so a later precautionary event can't
    // downgrade a real interruption into a silent refetch.
    if (
        !pendingRecovery ||
        (severity === "stream-interrupted" &&
            pendingRecovery.severity === "precautionary")
    ) {
        pendingRecovery = { store, reason, severity };
    }

    if (pendingRecoveryTimer !== null) return;

    // +50ms so the window has demonstrably passed; landing exactly on the
    // boundary would re-defer in a loop.
    const delay = Math.max(0, gapRecoveryCooldownUntil - Date.now()) + 50;
    pendingRecoveryTimer = setTimeout(() => {
        pendingRecoveryTimer = null;
        const queued = pendingRecovery;
        pendingRecovery = null;
        if (queued) {
            recoverStreamState(
                queued.store,
                `${queued.reason} (deferred)`,
                queued.severity,
            );
        }
    }, delay);
}

// Cached store references — lazy-initialized once to avoid per-message overhead
let _cachedVenueDataStore: ReturnType<typeof useVenueDataStore> | null = null;
let _cachedInstallerMapStore: ReturnType<typeof useInstallerMapStore> | null =
    null;
let _cachedToastService: ReturnType<typeof useToastService> | null = null;

function getCachedStores() {
    if (!_cachedVenueDataStore) _cachedVenueDataStore = useVenueDataStore();
    if (!_cachedInstallerMapStore)
        _cachedInstallerMapStore = useInstallerMapStore();
    if (!_cachedToastService) _cachedToastService = useToastService();
    return {
        venueDataStore: _cachedVenueDataStore,
        installerMapStore: _cachedInstallerMapStore,
        toastService: _cachedToastService,
    };
}

/** Venue name for a toast whose incidents all belong to a venue other than the one being viewed. */
function otherVenueName(
    message: EdgeStreamMessage,
    currentVenueId: number | null | undefined,
    venueDataStore: ReturnType<typeof useVenueDataStore>,
): string | undefined {
    const venueIds = new Set(
        (message.deltas?.incidents ?? [])
            .map((inc) => inc.venueId)
            .filter((id): id is number => typeof id === "number"),
    );
    if (venueIds.size !== 1) return undefined;
    const [venueId] = venueIds;
    if (venueId === currentVenueId) return undefined;
    return venueDataStore.allVenues.find((v) => v.id === venueId)?.name;
}

/**
 * Refetch state missed while not receiving messages; toast only when
 * `stream-interrupted`. Called by gap detection, and by the stream service on
 * reconnect and wake — where a gap is certain but no message may ever arrive to
 * prove it (a quiet venue sends none, and the server sometimes omits last_uuid).
 *
 * @returns true if it ran, false if deferred to the end of the cooldown.
 */
export function recoverStreamState(
    store: ViewContextStore,
    reason: string,
    severity: StreamRecoverySeverity = "stream-interrupted",
): boolean {
    if (Date.now() < gapRecoveryCooldownUntil) {
        console.log(`⏭️ Stream recovery deferred (cooldown): ${reason}`);
        deferRecovery(store, reason, severity);
        return false;
    }

    // This run covers anything queued behind it.
    if (pendingRecoveryTimer !== null) {
        clearTimeout(pendingRecoveryTimer);
        pendingRecoveryTimer = null;
    }
    pendingRecovery = null;

    gapRecoveryCooldownUntil = Date.now() + GAP_RECOVERY_COOLDOWN_MS;
    console.warn(`🔄 Recovering stream state [${severity}] — ${reason}`);

    const { venueDataStore, toastService } = getCachedStores();

    // Announcing a phantom reconnection on every alt-tab would train operators
    // to ignore the toast, so only speak up when something was actually wrong.
    if (severity === "stream-interrupted") {
        toastService.showToast({
            group: "secondarynotification",
            severity: ToastSeverity.WARN,
            summary: "Stream reconnected",
            detail: "Refreshing data to ensure accuracy",
            life: 3000,
            icon: "fas fa-arrows-rotate",
        });
    }

    for (const venue of venueDataStore.allVenuesList) {
        venueDataStore.fetchActiveIncidents(venue.id, true).catch((e) => {
            console.error(
                `[StreamRecovery] fetchActiveIncidents(${venue.id}) failed:`,
                e,
            );
        });
    }

    // User positions are the state that actually goes stale while the stream is
    // down — the symptom this recovery exists for — and nothing else here
    // refetches them: users are venue-scoped, so fetchLevelData (devices,
    // markers, spaces, buildings) never touches them. Only the viewed venue,
    // since a forced fetch paginates every user in it.
    if (store.venueId) {
        venueDataStore.fetchVenueUsers(store.venueId, true).catch((e) => {
            console.error("[StreamRecovery] fetchVenueUsers failed:", e);
        });
    }

    if (store.venueId && store.levelId) {
        venueDataStore
            .fetchLevelData(store.venueId, store.levelId, true)
            .catch((e) => {
                console.error("[StreamRecovery] fetchLevelData failed:", e);
            });
    }

    notify(recoveryListeners);

    return true;
}

/**
 * Handle new unified edge stream messages (stream/edge topic)
 * Supports delta updates and UUID-based message tracking
 */
export function handleEdgeStreamMessage(
    store: ViewContextStore,
    message: EdgeStreamMessage,
    router: Router,
) {
    try {
        console.log("⚡ Edge stream message received:", message);

        const { venueDataStore, installerMapStore, toastService } =
            getCachedStores();
        const { showToast } = toastService;

        // ============================================================
        // UUID Tracking - Detect Missed Messages
        // ============================================================

        if (message.uuid) {
            const chain = streamChainKey(message);
            const lastReceivedUuid = lastUuidByChain.get(chain);

            // 1. Skip if message already processed (deduplication)
            if (message.uuid === lastReceivedUuid) {
                console.log(
                    "⏭️ Skipping duplicate stream message:",
                    message.uuid,
                );
                return;
            }

            // 2. Check for sequence gaps. The cursor deliberately survives
            //    reconnects — that is the whole point of the check — and is
            //    cleared only on an explicit disconnect.
            if (
                lastReceivedUuid &&
                message.last_uuid &&
                message.last_uuid !== lastReceivedUuid
            ) {
                recoverStreamState(
                    store,
                    `sequence gap [${chain || "default"}]: expected last_uuid="${lastReceivedUuid}" but got "${message.last_uuid}"`,
                );
            }
            lastUuidByChain.set(chain, message.uuid);
        }

        // ============================================================
        // Ignored Scopes
        // ============================================================

        // scope=snapshot is a full-state broadcast this client does not
        // consume: nothing in it is applied — no deltas, no toast. The UUID
        // block above still ran on purpose, so the message stays part of our
        // chain; returning before it would leave the cursor behind and make
        // the *next* message look like a gap, firing a false refetch.
        if (message.scope === "snapshot") {
            console.log(
                "⏭️ Ignoring snapshot-scope stream message:",
                message.uuid,
            );
            return;
        }

        // ============================================================
        // Apply Delta Updates
        // ============================================================

        if (message.scope === "event") {
            for (const entry of message.deltas?.events ?? []) {
                notify(eventLogListeners, entry);
            }
        } else if (message.deltas) {
            // CV-1639: capture — BEFORE applyDelta mutates the stores —
            // whether the venue the operator is currently viewing already has
            // an active incident. This gates the auto-navigation below: we
            // only pull them into a new incident when their current venue had
            // none. Because the venue had zero active incidents beforehand,
            // any incident that is active for it after the delta necessarily
            // just arrived, so no per-incident "was it already active"
            // bookkeeping is needed.
            const currentVenueId = store.venueId;
            const hadActiveOnCurrentVenue =
                currentVenueId != null &&
                venueDataStore.getActiveIncidents(currentVenueId).length > 0;

            // Routing is the store's concern: applyDelta resolves venue/level
            // per-entity from the payload itself. No envelope plumbing here.
            venueDataStore.applyDelta({
                scope: message.scope,
                method: message.method,
                timestamp: message.timestamp,
                incidents: message.deltas.incidents,
                markers: message.deltas.markers,
                safeZones: message.deltas.safeZones,
                users: message.deltas.users,
                devices: message.deltas.devices,
                comments: message.deltas.comments,
                incidentSafeZones: message.deltas.incidentSafeZones,
                endUsersAssisting: message.deltas.endUsersAssisting,
                incidentDuplicates: message.deltas.incidentDuplicates,
            });

            // Handle beacon placement updates (installer view)
            // Support both camelCase and PascalCase for backward compatibility
            // Uses method-based approach (POST/PUT/PATCH/DELETE) like venueData.ts
            const beaconLocations =
                message.deltas.beaconPlacementLocations ||
                message.deltas.BeaconPlacementLocations;

            if (beaconLocations) {
                const method = message.method?.toUpperCase();

                if (method === "PUT") {
                    // PUT: Replace ALL state with delta
                    installerMapStore.setPlacements(
                        beaconLocations as BeaconPlacementLocation[],
                    );
                    console.log(
                        `📍 Beacon placements PUT (REPLACE ALL): ${beaconLocations.length} placements`,
                    );
                } else {
                    // POST/PATCH/DELETE: Apply incrementally
                    beaconLocations.forEach((location) => {
                        installerMapStore.applyPlacementByMethod(
                            location,
                            method,
                        );
                    });
                    console.log(
                        `📍 Beacon placements ${method} (incremental): ${beaconLocations.length} items`,
                    );
                }
            }

            // Check if user is viewing an incident that was just resolved
            if (message.deltas.incidents && store.incidentId) {
                const resolvedIncident = message.deltas.incidents.find(
                    (inc) =>
                        inc.id === store.incidentId &&
                        inc.status &&
                        inc.status !== "confirmed" &&
                        inc.status !== "unconfirmed",
                );

                if (resolvedIncident) {
                    console.log(
                        `[StreamHandler] Current incident ${store.incidentId} was resolved, redirecting to home`,
                    );
                    store.setIncidentId(null);
                    // Navigate to operator home with venue ID
                    if (store.venueId) {
                        router.push({
                            name: "operator-home",
                            params: { venueId: store.venueId },
                        });
                    } else {
                        router.push({ name: "operator-home-redirect" });
                    }
                }
            }

            // CV-1639: when the operator is on the current venue's home (not
            // already viewing an incident) and that venue had NO active
            // incident before this delta, pull them straight into a
            // newly-created incident for THIS venue. Because the venue had
            // zero active incidents beforehand, any incident that is active
            // for it now necessarily just arrived. Incidents on other venues
            // or communities never auto-navigate. Toasts — and any "View
            // Incident" action on them — are backend-driven via message.toast,
            // not generated here.
            if (
                message.deltas.incidents &&
                currentVenueId != null &&
                !store.incidentId &&
                !hadActiveOnCurrentVenue
            ) {
                const newIncident = message.deltas.incidents.find(
                    (inc) =>
                        (typeof inc.venueId === "number"
                            ? inc.venueId
                            : null) === currentVenueId &&
                        (inc.status === "confirmed" ||
                            inc.status === "unconfirmed"),
                );
                if (newIncident) {
                    store.setIncidentId(newIncident.id);
                    router.push({
                        name: "operator-incident-dashboard",
                        params: {
                            venueId: currentVenueId,
                            incidentId: newIncident.id,
                        },
                    });
                }
            }
        }

        // ============================================================
        // Display Toast
        // ============================================================

        if (message.toast?.show) {
            const severityMap = {
                success: ToastSeverity.SUCCESS,
                info: ToastSeverity.INFO,
                warn: ToastSeverity.WARN,
                error: ToastSeverity.ERROR,
            };

            showToast({
                group: "secondarynotification",
                severity: severityMap[message.toast.severity],
                venueName: otherVenueName(
                    message,
                    store.venueId,
                    venueDataStore,
                ),
                summary: message.toast.summary,
                detail: message.toast.detail,
                life: message.toast.life ?? 5000,
                icon: message.toast.icon,
                url: message.toast.url,
                label: message.toast.label,
            });
        }
    } catch (error) {
        console.error("Error handling edge stream message:", error, message);
        try {
            const { toastService } = getCachedStores();
            toastService.showToast({
                group: "secondarynotification",
                severity: ToastSeverity.ERROR,
                summary: "Error processing stream update",
                detail: "An error occurred while processing the real-time update",
                life: 5000,
            });
        } catch {
            /* toast service unavailable */
        }
    }
}

/**
 * Advance the UUID cursor to a known-good UUID without triggering gap detection.
 * Used by the RAF drain to keep the chain current for location-only messages whose
 * UUIDs were never individually passed through handleEdgeStreamMessage.
 */
export function setLastReceivedUuid(uuid: string, chain: string): void {
    lastUuidByChain.set(chain, uuid);
}

/**
 * Clear the message-sequence cursor and the recovery cooldown.
 *
 * ONLY call this when the session itself ends — an explicit disconnect,
 * signout, or service reset. Do NOT call it on reconnect: the cursor is what
 * lets the next message prove a gap occurred, and clearing it there is exactly
 * what stopped sleep/wake recovery from ever firing. A reconnect keeps the
 * cursor and asks recoverStreamState to refetch directly.
 */
export function resetStreamCursor() {
    lastUuidByChain.clear();
    gapRecoveryCooldownUntil = 0;
    // A deferred recovery must not fire after the session it belonged to.
    if (pendingRecoveryTimer !== null) {
        clearTimeout(pendingRecoveryTimer);
        pendingRecoveryTimer = null;
    }
    pendingRecovery = null;
    console.log("🔄 Edge stream cursor reset");
}

/**
 * Drop the memoised Pinia/toast references so they are re-resolved on next use.
 *
 * Separate from resetStreamCursor because it answers a different question: the
 * cursor is stream-sequence state, this is DI cache invalidation, needed only
 * when the Pinia instance behind these stores may have been torn down (signout,
 * test reset). Reconnects must not touch either.
 */
export function clearStreamStoreCache() {
    _cachedVenueDataStore = null;
    _cachedInstallerMapStore = null;
    _cachedToastService = null;
}
